import React, { forwardRef } from 'react';
import { Empty, Typography } from 'antd';
import SimpleVideoPlayer, { SimpleVideoPlayerHandle } from './SimpleVideoPlayer';
import { Panel } from '../types';

const { Text } = Typography;

interface IsolatedVideoPlayerProps {
  panels: Panel[];
  panelDurations: number[];
  audioPath?: string;
  backgroundMusicUrl?: string;
  backgroundVolume?: number;
  aspectRatio?: '16:9' | '9:16';
  mutedTracks?: {
    voice: boolean;
    bgm: boolean;
    sfx: boolean;
  };
  playbackSpeed?: number;
  hasTimestamps: boolean;
  audioDuration?: number | null;
}

/**
 * Isolated wrapper for SimpleVideoPlayer that prevents re-renders
 * when parent component state changes (like currentTime)
 */
export const IsolatedVideoPlayer = React.memo(
  forwardRef<SimpleVideoPlayerHandle, IsolatedVideoPlayerProps>(
    (
      {
        panels,
        panelDurations,
        audioPath,
        backgroundMusicUrl,
        backgroundVolume,
        aspectRatio,
        mutedTracks,
        playbackSpeed,
        hasTimestamps,
        audioDuration,
      },
      ref
    ) => {
      // console.log('[IsolatedVideoPlayer] Render', {
      //   panelsCount: panels.length,
      //   hasTimestamps,
      //   firstPanelId: panels[0]?.id,
      //   firstPanelMotionCount: panels[0]?.motionEffects?.length,
      //   firstPanelSfxCount: panels[0]?.soundEffects?.length
      // });

      if (!(panels.length > 0 && hasTimestamps)) {
        return (
          <Empty
            description={
              <Text style={{ color: '#999' }}>
                {panels.length === 0
                  ? 'No panels yet. Create panels first in Episodes tab.'
                  : 'No timestamps yet. Click "Analyze Timestamps" to start.'}
              </Text>
            }
            style={{ color: '#999' }}
          />
        );
      }

      // Generate key based on panel durations to force re-mount when they change
      const playerKey = panelDurations.join('-');

      return (
        <SimpleVideoPlayer
          key={playerKey}
          ref={ref}
          panels={panels}
          panelDurations={panelDurations}
          audioPath={audioPath}
          backgroundMusicUrl={backgroundMusicUrl}
          backgroundVolume={backgroundVolume}
          aspectRatio={aspectRatio}
          mutedTracks={mutedTracks}
          playbackSpeed={playbackSpeed}
          audioDuration={audioDuration}
        />
      );
    }
  ),
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if these props actually changed
    
    // Deep check panels (including imagePath, caption, motionEffects, soundEffects)
    const panelsEqual =
      prevProps.panels.length === nextProps.panels.length &&
      prevProps.panels.every((p, i) => {
        const next = nextProps.panels[i];
        if (!next) return false;
        
        // Quick checks first
        if (
          p.id !== next.id || 
          p.imagePath !== next.imagePath || 
          p.caption !== next.caption ||
          p.mediaType !== next.mediaType ||
          p.videoUrl !== next.videoUrl ||
          p.videoDuration !== next.videoDuration ||
          p.firstFrameUrl !== next.firstFrameUrl
        ) {
          return false;
        }
        
        // Check array lengths first (faster than stringify)
        const motionLenEqual = (p.motionEffects?.length || 0) === (next.motionEffects?.length || 0);
        const sfxLenEqual = (p.soundEffects?.length || 0) === (next.soundEffects?.length || 0);
        
        if (!motionLenEqual || !sfxLenEqual) {
          return false;
        }
        
        // Only stringify if lengths match (more expensive check)
        return (
          JSON.stringify(p.motionEffects) === JSON.stringify(next.motionEffects) &&
          JSON.stringify(p.soundEffects) === JSON.stringify(next.soundEffects)
        );
      });

    const durationsEqual =
      prevProps.panelDurations.length === nextProps.panelDurations.length &&
      prevProps.panelDurations.every((d, i) => d === nextProps.panelDurations[i]);

    const audioEqual = prevProps.audioPath === nextProps.audioPath;
    const bgmEqual = prevProps.backgroundMusicUrl === nextProps.backgroundMusicUrl;
    const volumeEqual = prevProps.backgroundVolume === nextProps.backgroundVolume;
    const aspectEqual = prevProps.aspectRatio === nextProps.aspectRatio;
    const speedEqual = prevProps.playbackSpeed === nextProps.playbackSpeed;
    const timestampsEqual = prevProps.hasTimestamps === nextProps.hasTimestamps;
    const audioDurationEqual = prevProps.audioDuration === nextProps.audioDuration;

    const mutedEqual =
      prevProps.mutedTracks?.voice === nextProps.mutedTracks?.voice &&
      prevProps.mutedTracks?.bgm === nextProps.mutedTracks?.bgm &&
      prevProps.mutedTracks?.sfx === nextProps.mutedTracks?.sfx;

    const allEqual =
      panelsEqual &&
      durationsEqual &&
      audioEqual &&
      bgmEqual &&
      volumeEqual &&
      aspectEqual &&
      speedEqual &&
      mutedEqual &&
      timestampsEqual &&
      audioDurationEqual;

    if (!allEqual) {
      console.log('[IsolatedVideoPlayer] Props changed - WILL RE-RENDER:', {
        panelsEqual,
        durationsEqual,
        audioEqual,
        bgmEqual,
        volumeEqual,
        aspectEqual,
        speedEqual,
        mutedEqual,
        timestampsEqual,
        audioDurationEqual,
      });
      
      // Log detailed panel changes if panels changed
      if (!panelsEqual) {
        console.log('[IsolatedVideoPlayer] Panel changes detected:');
        prevProps.panels.forEach((p, i) => {
          const next = nextProps.panels[i];
          if (!next) {
            console.log(`  Panel ${i}: REMOVED`);
            return;
          }
          
          const changes: string[] = [];
          if (p.id !== next.id) changes.push('id');
          if (p.imagePath !== next.imagePath) changes.push('imagePath');
          if (p.caption !== next.caption) changes.push('caption');
          if (p.mediaType !== next.mediaType) changes.push('mediaType');
          if (p.videoUrl !== next.videoUrl) changes.push('videoUrl');
          if (p.videoDuration !== next.videoDuration) changes.push('videoDuration');
          if (p.firstFrameUrl !== next.firstFrameUrl) changes.push('firstFrameUrl');
          if (JSON.stringify(p.motionEffects) !== JSON.stringify(next.motionEffects)) changes.push('motionEffects');
          if (JSON.stringify(p.soundEffects) !== JSON.stringify(next.soundEffects)) changes.push('soundEffects');
          
          if (changes.length > 0) {
            console.log(`  Panel ${i} (${p.id}): ${changes.join(', ')}`);
          }
        });
      }
    }
    // else {
    //   console.log('[IsolatedVideoPlayer] Props unchanged - SKIP re-render');
    // }

    return allEqual; // Return true to SKIP re-render
  }
);

IsolatedVideoPlayer.displayName = 'IsolatedVideoPlayer';
